// Small pieces shared by the acquisition pages: attribution and reliability badges, test states.

import type { Reliability, TestRecommendation, TestState } from '@growth/client';
import { Icon } from './ui.tsx';

export const ATTRIBUTION: Record<string, { label: string; help: string }> = {
  direct: { label: 'Directa', help: 'Jugadores cuya atribución en el SDK nombra la campaña, el grupo o la creatividad (por usuario).' },
  shared: { label: 'Compartida', help: 'Datos agregados de la red: el gasto y las instalaciones no se pueden repartir por jugador.' },
  directional: { label: 'Direccional', help: 'Reparto que hace Google dentro de las campañas de aplicaciones (por recurso): úsalo para ordenar, no como cifra exacta.' },
  blended: { label: 'Mezclada', help: 'Todos los jugadores nuevos (también orgánicos) frente al gasto total (eCPI mezclado).' },
};

export function AttributionBadge({ kind }: { kind: string }) {
  const a = ATTRIBUTION[kind] ?? { label: kind, help: '' };
  return (
    <span className="badge" title={a.help}>
      {a.label}
    </span>
  );
}

const RELIABILITY: Record<Reliability, { label: string; icon: 'good' | 'info' | 'warning' }> = {
  reliable: { label: 'Fiable', icon: 'good' },
  early: { label: 'Señal temprana', icon: 'info' },
  insufficient: { label: 'Insuficiente', icon: 'warning' },
};

export function ReliabilityBadge({ value, prefix }: { value: Reliability; prefix?: string }) {
  const r = RELIABILITY[value];
  return (
    <span className="badge" title="Fiable: ≥ 100 (instalaciones o jugadores maduros) · temprana: ≥ 30 · insuficiente: menos">
      <Icon name={r.icon} />
      {prefix ? `${prefix}: ` : ''}
      {r.label}
    </span>
  );
}

const STATES: Record<TestState, { label: string; icon: 'good' | 'info' | 'warning' }> = {
  INSUFFICIENT_DATA: { label: 'INSUFFICIENT DATA', icon: 'warning' },
  EARLY_SIGNAL: { label: 'EARLY SIGNAL', icon: 'info' },
  PROMISING: { label: 'PROMISING', icon: 'info' },
  LIKELY_WINNER: { label: 'LIKELY WINNER', icon: 'good' },
  CONFIRMED: { label: 'CONFIRMED', icon: 'good' },
};

export function StateBadge({ state, control }: { state: TestState | null; control?: boolean }) {
  if (!state) return <span className="badge">{control ? 'CONTROL' : '—'}</span>;
  const s = STATES[state];
  return (
    <span className="badge state">
      <Icon name={s.icon} />
      {s.label}
    </span>
  );
}

export const RECOMMENDATION: Record<TestRecommendation, { label: string; help: string }> = {
  stop: { label: 'STOP', help: 'Parar: el test no va a llegar a un resultado útil o es claramente malo.' },
  iterate: { label: 'ITERATE', help: 'Iterar: cambiar la parte que falla (creatividad, ficha de la tienda u onboarding) y volver a probar.' },
  test_again: { label: 'TEST AGAIN', help: 'Repetir: falta muestra o hay que confirmar el resultado antes de decidir.' },
  scale: { label: 'SCALE', help: 'Escalar: confirmado con CPI y retención. Requiere tu aprobación: la plataforma nunca cambia presupuestos.' },
};

/** Spanish text for the informational notes the API returns in English (unknown notes pass through). */
export function noteEs(note: string): string {
  const map: [string, string][] = [
    ['platform filter does not apply', 'El filtro de plataforma no se aplica a los datos de la red (una campaña de aplicaciones promociona una sola app).'],
    ['creative filter applies to the Creatives views', 'El filtro de creatividad se aplica en Creatividades; aquí los totales de la red no se filtran por creatividad.'],
    ['country filter ignored for ad-network numbers', 'El filtro de país no se aplica a la red: ningún informe tiene a la vez grupo de anuncios y país.'],
    ['country filter: ad-network numbers come from the user-location report', 'Filtro de país: los datos de la red salen del informe de ubicación de los usuarios (dónde estaban).'],
    ['Game steps include every new install', 'Los pasos del juego incluyen todas las instalaciones nuevas, también orgánicas. Marca «Solo jugadores que el SDK atribuye a Google Ads» para seguir solo a los de pago.'],
  ];
  return map.find(([en]) => note.startsWith(en))?.[1] ?? note;
}

/** Spanish wording of the fatigue / attention signals the API returns. */
export function signalEs(signal: string): string {
  let m = /^CTR fell (\d+)%/.exec(signal);
  if (m) return `El CTR cayó un ${m[1]} % (significativo al 95 %)`;
  m = /^IPM fell (\d+)%/.exec(signal);
  if (m) return `El IPM cayó un ${m[1]} % (intervalos sin solapar)`;
  m = /^CPI rose (\d+)%/.exec(signal);
  if (m) return `El CPI subió un ${m[1]} %`;
  m = /^CPI ([\d.]+) above target ([\d.]+)/.exec(signal);
  if (m) return `CPI ${m[1]!.replace('.', ',')} por encima del objetivo ${m[2]!.replace('.', ',')} (todo el intervalo)`;
  return signal;
}

export const FATIGUE_NOTE_ES =
  'Posible fatiga creativa: el rendimiento cayó frente a los 7 días anteriores. Es una correlación, no una causa: revisa frecuencia, saturación de la audiencia, estacionalidad, pujas, presupuesto y competencia antes de sustituir la creatividad.';
